// asyncJob — small helper for the async-job pattern used by long-running
// LLM endpoints. Feb 2026 (Randy pt-ytre-async-jobs) — production's
// ingress kills any HTTP request >~60s. YTRE's LLM calls routinely spend
// 60-90s inside the provider, so the three heaviest YTRE endpoints
// (ask-silo, redraft, master-reply) return a `{job_id, status: "pending"}`
// ticket. Feb 28 2026 v6 (Randy Chore-Async) — same pattern extended to
// calendar/chore-suggestions and generalized so any endpoint following
// the `{job_id}` contract can reuse it via `pollBase`.
//
// This helper hides the polling loop so callers keep reading like a
// normal awaited api.post — throws on error (mirroring what the sync
// endpoint used to do), returns the `result` payload on done.
import { api } from "@/lib/api";

/**
 * Kick off a POST that returns {job_id, status}, then poll until done.
 *
 * @param {string} startUrl  e.g. `/ytre/sessions/${sid}/ask-silo`
 * @param {object} startBody body posted to startUrl
 * @param {object} [opts]
 * @param {number} [opts.intervalMs=3000]  poll cadence
 * @param {number} [opts.timeoutMs=360000] client-side ceiling. Sep 12
 *   2026 (Randy) — was 180s. A production pod embedding a 240-window
 *   transcript for the first time needs longer than that, and the job
 *   reports live progress the whole way, so giving up at 3 minutes threw
 *   away work that was about to succeed. 6 minutes is the safety net;
 *   it only fires if the background job truly hung.
 * @param {AbortSignal} [opts.signal] optional cancellation (e.g. user
 *   closed the modal). Cancels only the polling; the server job
 *   keeps running so the result is still recoverable via job_id.
 * @param {string} [opts.pollBase="/ytre/jobs"] path prefix for the
 *   polling endpoint. Feb 28 2026 v6 — added so non-YTRE callers can
 *   use the same helper. Calendar/chore-suggestions passes
 *   `/calendar/jobs`.
 * @param {function} [opts.onProgress] optional callback receiving the
 *   `progress` field on each poll response (YTRE master-reply uses it
 *   to render "k of N families done").
 * @returns Promise<result payload>  — the `result` field from the
 *   done poll response. Throws Error with `.response = {data:{detail}}`
 *   on error to match axios's shape so existing catch blocks work.
 */
export async function runAsyncJob(startUrl, startBody = null, opts = {}) {
  const intervalMs = opts.intervalMs || 3000;
  const timeoutMs = opts.timeoutMs || 360000;
  const signal = opts.signal || null;
  const pollBase = opts.pollBase || "/ytre/jobs";

  const r = await api.post(startUrl, startBody);
  const job_id = r.data?.job_id;
  if (!job_id) {
    // Backward-compat: sync endpoint returned an inline result. Pass through.
    return r.data;
  }
  const started = Date.now();
  await new Promise((res) => setTimeout(res, 900));
  while (true) {
    if (signal?.aborted) {
      throw new Error("Cancelled");
    }
    if (Date.now() - started > timeoutMs) {
      throw new Error("This is taking longer than expected. Try again in a moment.");
    }
    let pollResp;
    try {
      pollResp = await api.get(`${pollBase}/${job_id}`);
    } catch (e) {
      if (e?.response?.status === 404) throw e;
      await new Promise((res) => setTimeout(res, intervalMs));
      continue;
    }
    const p = pollResp.data || {};
    if (p.status === "done") return p.result;
    if (p.status === "error") {
      const err = new Error(p.error || "The background job failed.");
      err.response = { data: { detail: p.error || "The background job failed." }, status: p.http_status || 500 };
      throw err;
    }
    if (p.progress && typeof opts.onProgress === "function") {
      try { opts.onProgress(p.progress); } catch { /* ignore */ }
    }
    await new Promise((res) => setTimeout(res, intervalMs));
  }
}

// Feb 28 2026 v6 — backward-compat alias for existing YTRE callers.
// Same behavior, just keeps old imports resolving. New callers use
// `runAsyncJob` and pass `opts.pollBase` explicitly.
export const runYtreAsyncJob = runAsyncJob;
